"use client";

import { Container, SectionSubHeading, SplitText } from "@/components";
import { ProjectScopeTab } from "@/lib/types";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils/utils";

const CaseStudyProjectScope = ({
  projectScope,
  title,
}: {
  projectScope: ProjectScopeTab[];
  title: string;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  useGSAP(
    () => {
      ScrollTrigger.refresh();
      const sections = gsap.utils.toArray<HTMLElement>(".scope-section");
      sections.forEach((section, index) => {
        ScrollTrigger.create({
          trigger: section,
          start: "top 50%",
          end: "bottom 50%",
          onEnter: () => setActiveTab(index),
          onEnterBack: () => setActiveTab(index),
        });

        const chars = section.querySelectorAll(".reveal-char");

        gsap.set(chars, { opacity: 0.2 });
        gsap.to(chars, {
          opacity: 1,
          stagger: 0.05,
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "top 40%",
            scrub: 1.5,
          },
        });
      });
    },
    { scope: sectionRef },
  );

  // Mobile Horizontal Navigation Auto-Scroll Sync
  useEffect(() => {
    if (mobileNavRef.current) {
      const activeBtn = mobileNavRef.current.children[activeTab] as HTMLElement;
      if (activeBtn) {
        const container = mobileNavRef.current;
        const scrollLeft =
          activeBtn.offsetLeft -
          container.clientWidth / 2 +
          activeBtn.clientWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: "smooth" });
      }
    }
  });
  return (
    <section ref={sectionRef} className="bg-accent-foreground lg:py-10 py-4">
      <Container className="px-4 lg:px-10">
        {/* Section Label */}

        <SectionSubHeading
          subtitle="Project Scope "
          className="text-muted-foreground mb-10"
        />

        <div className="flex flex-col lg:flex-row gap-10 items-start w-full mt-0 relative">
          {/* Tab Navigation */}
          {/* MOBILE FLOATING NAVIGATION */}
          <div className="lg:hidden sticky top-20 z-5 w-full bg-accent-foreground/80 backdrop-blur-md">
            <div
              ref={mobileNavRef}
              className="flex overflow-x-auto no-scrollbar"
            >
              {projectScope.map((item, index) => (
                <button
                  key={index}
                  onClick={() =>
                    gsap.to(window, { scrollTo: `#scope-section-${index}` })
                  }
                  className={cn(
                    "shrink-0 w-48 px-4 py-2 text-lg uppercase transition-all border font-bold",
                    activeTab === index
                      ? "bg-primary text-accent-foreground"
                      : "text-white",
                  )}
                >
                  {item.header}
                </button>
              ))}
            </div>
          </div>
          {/*  DESKTOP STICKY SIDEBAR */}
          <aside className="w-full sticky lg:w-80 hidden lg:flex lg:flex-col overflow-x-auto top-10 lg:overflow-x-visible no-scrollbar border border-white">
            {projectScope.map((item, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`shrink-0 w-48 lg:w-full h-20 lg:h-24 px-6 text-left uppercase text-base lg:text-2xl border-l border-b border-white font-black transition-all cursor-pointer
                  ${activeTab === index ? "bg-primary text-accent-foreground" : "text-white"}`}
              >
                {item.header}
              </button>
            ))}
          </aside>

          {/* Tab Content Area */}
          <div className="grow w-full overflow-hidden">
            {projectScope.map((item, index) => (
              <div
                key={index}
                id={`scope-section-${index}`}
                className={cn(
                  "scope-section space-y-12 transition-all duration-700 lg:min-h-[70vh]",
                  [2, 3].includes(index) ? "pt-16 lg:pt-8" : "pt-4",
                  activeTab === index ? "opacity-100  " : "opacity-0",
                )}
              >
                {/* Grid of Images (Top Row) */}
                {item?.images && item.images.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 md:gap-5">
                    {item.images.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-video lg:h-130 w-full overflow-hidden md:h-100 h-40"
                      >
                        <Image
                          src={img}
                          alt={`${title} — project scope image ${idx + 1}`}
                          width={1920}
                          quality={100}
                          sizes="100vw"
                          className="relative w-full h-full"
                        />
                      </div>
                    ))}
                  </div>
                )}
                {/* Description Text (Middle Section) */}
                <h3 className="text-white text-base md:text-xl lg:text-3xl font-medium lg:leading-10 max-w-237.5 mobile-card">
                  {SplitText(item?.desc || "")}
                </h3>
                {/*  Single Wide Image (Bottom Section) */}
                {item?.image && (
                  <div className="relative md:aspect-video h-50 md:h-100 lg:h-120 w-full overflow-hidden">
                    <Image
                      src={item.image}
                      alt={`${title} — project scope overview`}
                      width={1920}
                      quality={100}
                      sizes="100vw"
                      className="relative w-full h-full"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default CaseStudyProjectScope;
